import * as Api from '@ViewModels';
import { UserSessionContext } from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const QUERY_KEY = `tag/search`;

interface TagSearchQueryArgs {
	query: string;
	sortDescriptor?: Api.ISortDescriptor;
	expandTagAlert?: boolean;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

// Single source of truth for the tag/search queryKey + fetcher. Consumers using
// useQueries (which can't compose with custom hooks) call this directly so the
// cache shape stays in sync with useTagSearchQuery.
export function getTagSearchQueryOptions(
	userSession: UserSessionContext,
	{ query, sortDescriptor, expandTagAlert = true, impersonationContext, enabled }: TagSearchQueryArgs
) {
	return {
		queryKey: [QUERY_KEY, query, sortDescriptor, expandTagAlert, impersonationContext],
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<Api.IAccountTag[]>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `tag/search`,
					queryParams: {
						query,
						sort: sortDescriptor?.sort,
						sortBy: sortDescriptor?.sortBy,
						expand: expandTagAlert ? 'TagAlert' : undefined,
					},
				}),
				'GET'
			);
		},
		enabled,
	};
}

export function useTagSearchQuery(args: TagSearchQueryArgs) {
	const userSession = useUserSession();
	return useQuery(getTagSearchQueryOptions(userSession, args));
}

export function invalidateAllTagSearchQueries() {
	return reactQueryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
}
